import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuthStore } from "@/store/auth.store";
import { useRecordingSettingsStore } from "@/store/recordingSettings.store";
import { useTranscriptionStreamingStore } from "@/store/transcriptionStreaming.store";
import { AudioChunkAccumulator } from "./AudioChunkAccumulator";
import { CaptureCoverage, isCaptureCoverageLow } from "./captureCoverage";
import { TranscriberClientEvents, TranscriberServerEvents } from "./constants";
import { transcribeGapClip } from "./gapClip";
import { type PcmCapture, startPcmCapture } from "./pcmCapture";
import { PcmHistory } from "./pcmHistory";
import {
  type AudioRecoveryReason,
  type TranscriptCompletedMessage,
  type TranscriptEndMessage,
  TranscriptionProviderName,
  type TranscriptPartialMessage,
} from "./types";
import type { IUseAudioTranscription } from "./useAudioTranscriptionFactory";
import { useSocketIoClient } from "./useSocketIoClient";

/** Keep Deepgram WS alive while paused (24 kHz mono PCM16). Do not use for OpenAI — zeros trigger VAD commits. */
const SILENCE_KEEPALIVE_SAMPLE_RATE = 24000;
const SILENCE_KEEPALIVE_INTERVAL_MS = 1000;
const SILENT_PCM16_1S = new Int16Array(SILENCE_KEEPALIVE_SAMPLE_RATE);
/** Live-transcript redial after a lost connection: first wait, doubled per try, capped. */
export const LIVE_RECONNECT_BASE_MS = 1_000;
export const LIVE_RECONNECT_MAX_MS = 30_000;
/**
 * Recording starts as soon as the microphone is open; what is said while the
 * transcriber connects is held and sent first once the provider is ready.
 * Past this much the provider is late enough (the recorder's 10 s deadline)
 * that the full recording is transcribed after Stop instead.
 */
export const PRE_READY_MAX_SECONDS = 20;
/** One audio:chunk body (as the live stream sends, about 1.7 s of PCM16). */
const CHUNK_BYTES = 80000;
const CHUNK_SAMPLES = CHUNK_BYTES / 2;
/** The capture's rate (pcmCapture asks the AudioContext for 24 kHz). */
const CAPTURE_SAMPLE_RATE = 24000;
/**
 * After a reconnect, what the live transcript missed (a locked screen, a
 * frozen page, a network drop) is sent to the new session first, so its
 * lines appear in order. A longer gap goes to the API on its own
 * (GAP_CLIP_MAX_SECONDS).
 *
 * The gap goes through the same stream as the new audio, so new lines wait
 * until it is through: at `speed` times real time, a gap of G seconds takes
 * G / (speed - 1) seconds to catch up. Deepgram processes a live stream at
 * most 1.25x real time (its "recovering from connection errors" guide), so
 * only short gaps go live there (20 s catches up in 80 s); OpenAI's
 * realtime buffer has no documented limit and is sent at 5x (60 s in 15 s).
 */
export const BACKFILL_MAX_SECONDS = 180;
export const BACKFILL_LIMITS: Record<
  TranscriptionProviderName,
  { speed: number; maxSeconds: number }
> = {
  [TranscriptionProviderName.OPENAI]: {
    speed: 5,
    maxSeconds: BACKFILL_MAX_SECONDS,
  },
  [TranscriptionProviderName.DEEPGRAM]: { speed: 1.25, maxSeconds: 20 },
  [TranscriptionProviderName.SPEECHMATIC]: { speed: 1.25, maxSeconds: 20 },
};
/**
 * A gap the live provider cannot catch up on (longer than its limit above)
 * is transcribed on its own by the API, from the audio kept on the device,
 * and its lines are put in place when they come back. Up to this long; a
 * longer one gets the "Paused …" marker.
 */
export const GAP_CLIP_MAX_SECONDS = 300;
/**
 * Audio sent shortly before the connection was lost may never have been
 * transcribed (the provider's open segment, or a transport that was already
 * dead): from this long before the last word from the server, it is sent
 * again unless a finished segment arrived after it.
 */
export const BACKFILL_IN_FLIGHT_SECONDS = 10;
/**
 * The capture missed more than this of the gap (the page and its audio were
 * frozen): its audio cannot be sent again, so the gap is shown as paused.
 */
const BACKFILL_MAX_MISSING_SECONDS = 3;
/**
 * Sessions lost in a row while sending a gap: past this, the next one
 * starts with new audio and the gap goes to the API instead (a provider
 * that drops fast audio cannot keep the live transcript from coming back).
 */
const BACKFILL_MAX_FAILURES = 2;
/** No audio for this long (wall clock) while recording: the page was frozen. */
const STALL_MS = 2_500;
/** After a freeze, audio queued meanwhile lands within this. */
const STALL_GRACE_MS = 2_000;
/** "Adding …" goes when the gap's last line is in, or this long after it was sent. */
export const BACKFILL_SETTLE_MS = 20_000;

/**
 * The live-transcript session:
 * - connecting: the first session is not ready yet; audio is held.
 * - live: the provider is ready; audio streams and text is shown.
 * - reconnecting: the connection was lost while recording; the recording
 *   carries on and the transcript is paused until a new session is ready.
 * - closed: no live transcript any more (Stop, a final choice of the full
 *   recording, or reset).
 */
type LivePhase = "connecting" | "live" | "reconnecting" | "closed";

const reconnectDelay = (attempt: number): number =>
  Math.min(LIVE_RECONNECT_MAX_MS, LIVE_RECONNECT_BASE_MS * 2 ** attempt);

export const useAudioTranscription = (): IUseAudioTranscription => {
  const { socket, routeError, redial } = useSocketIoClient();
  const [transcription, setTranscription] = useState<string>("");
  const [finished, setFinished] = useState(false);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const captureRef = useRef<PcmCapture | null>(null);
  const coverageRef = useRef(new CaptureCoverage());
  const accumulatorRef = useRef<AudioChunkAccumulator>(
    new AudioChunkAccumulator(CHUNK_BYTES),
  );
  const phaseRef = useRef<LivePhase>("connecting");
  /** Recorded (not paused) seconds of audio so far, live or not. */
  const capturedSecondsRef = useRef(0);
  /** Where the current session's audio starts in the recording. */
  const sessionOffsetRef = useRef(0);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);
  const dialingRef = useRef(false);
  /** PCM samples held while connecting. */
  const heldSamplesRef = useRef(0);
  /** Recent audio by recording position, to send a gap again. */
  const historyRef = useRef(
    new PcmHistory(
      (Math.max(BACKFILL_MAX_SECONDS, GAP_CLIP_MAX_SECONDS) +
        BACKFILL_IN_FLIGHT_SECONDS) *
        CAPTURE_SAMPLE_RATE,
    ),
  );
  const sampleRateRef = useRef(CAPTURE_SAMPLE_RATE);
  /** Recording position just after the last sample sent to this session. */
  const sentSampleRef = useRef(0);
  /** Sent position when the last finished segment arrived. */
  const ackSampleRef = useRef(0);
  /** Sent position when the server was last heard from at all. */
  const aliveSampleRef = useRef(0);
  /**
   * Recording time (wall clock, pauses excluded) minus captured audio, when
   * the server was last heard from. If it has grown since, the capture
   * missed that much (a page frozen with its audio).
   */
  const clockLagRef = useRef(0);
  /** Wall time of the last captured audio, to notice a frozen page. */
  const lastPcmAtRef = useRef<number | null>(null);
  /** No coverage verdict until then: audio queued while frozen is landing. */
  const stallGraceUntilRef = useRef(0);
  /** A gap being sent to the new session (paced, ahead of new audio). */
  const backfillRef = useRef<{ gapId: number; to: number } | null>(null);
  const backfillTimerRef = useRef<number | null>(null);
  const backfillFailuresRef = useRef(0);
  /** The "Adding …" line still shown, until the gap's lines are in. */
  const settlingGapRef = useRef<{ gapId: number; to: number } | null>(null);
  const settleTimerRef = useRef<number | null>(null);
  const hiddenAtRef = useRef<number | null>(null);
  /** Gap clips being transcribed by the API, by gap id. */
  const gapClipsRef = useRef(new Map<number, AbortController>());
  const [liveTranscriptPaused, setLiveTranscriptPaused] = useState(false);
  const {
    setStreamText,
    setPartialText,
    settlePartialText,
    clearPartialText,
    resetStreamText,
    finishSteamText,
  } = useTranscriptionStreamingStore.getState();
  const {
    inputLanguage,
    noiseReduction,
    transcriptionProvider,
    noiseSuppression,
    diarize,
  } = useRecordingSettingsStore.getState();
  const isAudioTranscriptionPaused = useRef(true);
  const silenceKeepAliveTimerRef = useRef<number | null>(null);
  const finishedRef = useRef(false);
  const stoppingRef = useRef(false);
  const acceptingTranscriptRef = useRef(true);
  const recoveryRef = useRef(false);
  const [requiresAudioRecovery, setRequiresAudioRecovery] = useState(false);
  const [audioRecoveryReason, setAudioRecoveryReason] =
    useState<AudioRecoveryReason | null>(null);
  const { userData } = useAuthStore.getState();
  const [isProviderReady, setIsProviderReady] = useState(false);

  const submitChunk = (isLast: boolean = false): void => {
    const accumulator = accumulatorRef.current;
    const pcm16 = accumulator.merged;
    socket.emit(TranscriberClientEvents.AUDIO_CHUNK, {
      body: pcm16,
      isLast: isLast,
    });
    sentSampleRef.current += pcm16.length;
    accumulator.reset();
  };

  /** The server answered: audio sent before now reached it. */
  const clockLag = (): number =>
    coverageRef.current.report().activeSeconds -
    historyRef.current.end / sampleRateRef.current;

  const markAlive = (): void => {
    if (phaseRef.current !== "live") return;
    aliveSampleRef.current = sentSampleRef.current;
    clockLagRef.current = clockLag();
    trimHistory();
  };

  /** A finished segment arrived: audio sent before now was transcribed. */
  const markAcknowledged = (): void => {
    if (phaseRef.current !== "live") return;
    ackSampleRef.current = sentSampleRef.current;
    aliveSampleRef.current = sentSampleRef.current;
    clockLagRef.current = clockLag();
    trimHistory();
  };

  /** Where a new session has to start so nothing is missed (or repeated). */
  const rewindSample = (): number =>
    Math.max(
      ackSampleRef.current,
      aliveSampleRef.current -
        BACKFILL_IN_FLIGHT_SECONDS * sampleRateRef.current,
      0,
    );

  const trimHistory = (): void => {
    historyRef.current.trimBefore(rewindSample());
  };

  /** The audio held while connecting, in normal-sized chunks. */
  const flushHeldAudio = (): void => {
    heldSamplesRef.current = 0;
    const held = accumulatorRef.current.merged;
    accumulatorRef.current.reset();
    const samplesPerChunk = CHUNK_BYTES / 2;
    for (let start = 0; start < held.length; start += samplesPerChunk) {
      const body = held.slice(start, start + samplesPerChunk);
      if (body.length < samplesPerChunk) {
        // The remainder goes with the next live audio.
        accumulatorRef.current.addPcm(body);
        break;
      }
      socket.emit(TranscriberClientEvents.AUDIO_CHUNK, { body, isLast: false });
      sentSampleRef.current += body.length;
    }
  };

  /**
   * Closes the transport without anything queued for it: socket.io-client
   * would otherwise send chunks buffered while disconnected ahead of the next
   * session's audio:start.
   */
  const dropSocket = useCallback((): void => {
    (socket as { sendBuffer?: unknown[] }).sendBuffer = [];
    socket.disconnect();
  }, [socket]);

  const clearReconnect = useCallback((): void => {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  /** Ends a gap's sending and its "Adding …" line. */
  const clearBackfill = useCallback((): void => {
    if (backfillTimerRef.current !== null) {
      window.clearTimeout(backfillTimerRef.current);
      backfillTimerRef.current = null;
    }
    if (settleTimerRef.current !== null) {
      window.clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
    const { removeGap } = useTranscriptionStreamingStore.getState();
    for (const gap of [backfillRef.current, settlingGapRef.current])
      if (gap) removeGap(gap.gapId);
    backfillRef.current = null;
    settlingGapRef.current = null;
  }, []);

  /** Stop, reset or unmount: gap clips still out are not needed any more. */
  const abortGapClips = useCallback((): void => {
    const { removeGap } = useTranscriptionStreamingStore.getState();
    for (const [gapId, controller] of gapClipsRef.current) {
      controller.abort();
      removeGap(gapId);
    }
    gapClipsRef.current.clear();
  }, []);

  const stopSilenceKeepAlive = useCallback((): void => {
    if (silenceKeepAliveTimerRef.current !== null) {
      window.clearInterval(silenceKeepAliveTimerRef.current);
      silenceKeepAliveTimerRef.current = null;
    }
  }, []);

  /** Deepgram-only: zero PCM keepalive. OpenAI treats silence as end-of-utterance. */
  const startSilenceKeepAlive = (): void => {
    if (
      phaseRef.current !== "live" ||
      transcriptionProvider !== TranscriptionProviderName.DEEPGRAM
    ) {
      return;
    }
    if (silenceKeepAliveTimerRef.current !== null) {
      return;
    }
    silenceKeepAliveTimerRef.current = window.setInterval(() => {
      if (
        !isAudioTranscriptionPaused.current ||
        !socket.connected ||
        phaseRef.current !== "live" ||
        // Silence would land in the middle of the gap being sent.
        backfillRef.current
      ) {
        return;
      }
      socket.emit(TranscriberClientEvents.AUDIO_CHUNK, {
        body: SILENT_PCM16_1S,
        isLast: false,
      });
    }, SILENCE_KEEPALIVE_INTERVAL_MS);
  };

  /**
   * Stop the PCM capture only. Do NOT stop MediaStream tracks — they are
   * shared with react-media-recorder; stopping them first yields no blob and
   * a blank Stop-and-Save screen.
   */
  const teardownProcessor = useCallback((): void => {
    if (captureRef.current) {
      captureRef.current.stop();
      captureRef.current = null;
    }
    audioStreamRef.current = null;
  }, []);

  const reset = (): void => {
    stopSilenceKeepAlive();
    clearReconnect();
    clearBackfill();
    abortGapClips();
    teardownProcessor();
    accumulatorRef.current.reset();
    phaseRef.current = "closed";
    capturedSecondsRef.current = 0;
    heldSamplesRef.current = 0;
    sessionOffsetRef.current = 0;
    historyRef.current.reset();
    sentSampleRef.current = 0;
    ackSampleRef.current = 0;
    aliveSampleRef.current = 0;
    clockLagRef.current = 0;
    lastPcmAtRef.current = null;
    stallGraceUntilRef.current = 0;
    backfillFailuresRef.current = 0;
    reconnectAttemptRef.current = 0;
    setLiveTranscriptPaused(false);
    finishedRef.current = false;
    stoppingRef.current = false;
    acceptingTranscriptRef.current = false;
    recoveryRef.current = false;
    coverageRef.current.reset();
    setRequiresAudioRecovery(false);
    setAudioRecoveryReason(null);
    setTranscription("");
    setFinished(false);
    resetStreamText();
  };

  const reconnectRef = useRef<() => void>(() => undefined);

  /**
   * Live text can no longer be the note source: the full recording is
   * transcribed after Stop (a permanent choice for this recording). A lost
   * connection ("unavailable") only pauses the live transcript: it redials
   * with back-off while recording, so the doctor keeps seeing words. The
   * recording and its on-device copy never depend on the socket.
   */
  const latchRecovery = useCallback(
    (reason: AudioRecoveryReason): void => {
      if (!acceptingTranscriptRef.current || finishedRef.current) return;
      if (!recoveryRef.current) {
        recoveryRef.current = true;
        setRequiresAudioRecovery(true);
        setAudioRecoveryReason(reason);
        setTranscription("");
      }
      const phase = phaseRef.current;
      if (phase === "reconnecting" || phase === "closed") return;
      // Mid-visit, the live transcript (display only from now on) comes
      // back with a new session after either: the part it missed is added
      // again, or marked as paused when the capture missed it too.
      const reconnect = !stoppingRef.current;
      phaseRef.current = reconnect ? "reconnecting" : "closed";
      // A gap still being sent is sent again (from what was acknowledged)
      // by the next session.
      if (backfillRef.current) backfillFailuresRef.current += 1;
      clearBackfill();
      accumulatorRef.current.reset();
      setIsProviderReady(false);
      stopSilenceKeepAlive();
      finishSteamText();
      setLiveTranscriptPaused(reconnect);
      // This session has nothing left to do. Closing it ends any server
      // session and its reservation; a redial asks for a new one.
      dropSocket();
      if (reconnect) reconnectRef.current();
    },
    [finishSteamText, stopSilenceKeepAlive, dropSocket, clearBackfill],
  );

  /** The connection failed (again): try again later, or latch recovery. */
  const connectionLost = useCallback((): void => {
    if (phaseRef.current === "reconnecting") {
      dropSocket();
      reconnectRef.current();
      return;
    }
    latchRecovery("unavailable");
  }, [dropSocket, latchRecovery]);

  const attemptReconnect = async (): Promise<void> => {
    reconnectTimerRef.current = null;
    if (
      phaseRef.current !== "reconnecting" ||
      stoppingRef.current ||
      !acceptingTranscriptRef.current ||
      dialingRef.current ||
      socket.connected
    )
      return;
    // Offline: wait (the "online" event dials straight away; the timer
    // covers a browser that never sends it).
    if (navigator.onLine === false) {
      reconnectRef.current();
      return;
    }
    dialingRef.current = true;
    const dialed = await redial().catch(() => false);
    dialingRef.current = false;
    if (!dialed && phaseRef.current === "reconnecting") reconnectRef.current();
  };

  const scheduleReconnect = (): void => {
    if (
      phaseRef.current !== "reconnecting" ||
      reconnectTimerRef.current !== null ||
      dialingRef.current
    )
      return;
    const delay = reconnectDelay(reconnectAttemptRef.current);
    reconnectAttemptRef.current += 1;
    reconnectTimerRef.current = window.setTimeout(() => {
      void attemptReconnect();
    }, delay);
  };
  reconnectRef.current = scheduleReconnect;

  useEffect(() => {
    // The browser knows about the network before the socket does: pause the
    // live transcript at once, and redial as soon as the network is back.
    const onOffline = (): void => {
      const phase = phaseRef.current;
      if (phase === "live" || phase === "connecting") connectionLost();
    };
    const onOnline = (): void => {
      if (phaseRef.current !== "reconnecting") return;
      clearReconnect();
      reconnectAttemptRef.current = 0;
      void attemptReconnect();
    };
    // A locked phone (iOS Safari freezes the page) or a frozen background
    // tab: no timers run, so neither the back-off nor the heartbeat notices
    // the lost connection. On the way back, check the connection at once
    // and redial straight away.
    const onHide = (): void => {
      hiddenAtRef.current ??= Date.now();
    };
    const onResume = (): void => {
      if (document.visibilityState === "hidden") return;
      if (hiddenAtRef.current === null) return;
      hiddenAtRef.current = null;
      const phase = phaseRef.current;
      if (phase === "reconnecting") {
        clearReconnect();
        reconnectAttemptRef.current = 0;
        void attemptReconnect();
        return;
      }
      if (phase !== "live" && phase !== "connecting") return;
      if (!socket.connected) {
        connectionLost();
        return;
      }
      // A heartbeat that expired while frozen closes the transport now
      // (and the disconnect handler redials), not at the next timer.
      (
        socket.io as
          | { engine?: { _hasPingExpired?: () => boolean } }
          | undefined
      )?.engine?._hasPingExpired?.();
    };
    const onVisibility = (): void => {
      if (document.visibilityState === "hidden") onHide();
      else onResume();
    };
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibility);
    document.addEventListener("freeze", onHide);
    document.addEventListener("resume", onResume);
    window.addEventListener("pagehide", onHide);
    window.addEventListener("pageshow", onResume);
    // The server's heartbeat also says the connection is alive.
    const manager = (
      socket as {
        io?: {
          on?: (event: string, fn: () => void) => void;
          off?: (event: string, fn: () => void) => void;
        };
      }
    ).io;
    const onPing = (): void => markAliveRef.current();
    manager?.on?.("ping", onPing);
    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisibility);
      document.removeEventListener("freeze", onHide);
      document.removeEventListener("resume", onResume);
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("pageshow", onResume);
      manager?.off?.("ping", onPing);
    };
  });

  // Socket handlers pass event payloads; never forward them as a reason.
  const recoverAudio = useCallback(
    (): void => connectionLost(),
    [connectionLost],
  );

  /**
   * The live text may only become the note source when the live audio covers
   * the recording. Otherwise the full recording is transcribed after Stop.
   */
  const checkCaptureCoverage = (): boolean => {
    if (recoveryRef.current) return false;
    // Right after a freeze the queued audio is still arriving; until it has,
    // the live text has not missed anything (yet).
    if (!stoppingRef.current && performance.now() < stallGraceUntilRef.current)
      return true;
    if (!isCaptureCoverageLow(coverageRef.current.report())) return true;
    latchRecovery("incomplete");
    return false;
  };

  useEffect(() => {
    if (routeError) recoverAudio();
  }, [routeError, recoverAudio]);

  const finishSession = (): void => {
    stoppingRef.current = true;
    stopSilenceKeepAlive();
    clearReconnect();
    clearBackfill();
    abortGapClips();
    setLiveTranscriptPaused(false);
    isAudioTranscriptionPaused.current = false;
    coverageRef.current.pause();
    if (!recoveryRef.current) {
      if (phaseRef.current === "live" && socket.connected) {
        if (checkCaptureCoverage()) submitChunk(true);
      } else {
        // The live session never got ready (or just dropped): the live text
        // cannot be complete.
        latchRecovery("unavailable");
      }
    }
    if (recoveryRef.current && phaseRef.current !== "closed") {
      // A resumed live transcript was for display only.
      phaseRef.current = "closed";
      setIsProviderReady(false);
      dropSocket();
    }
    teardownProcessor();
    finishSteamText();
  };

  const startSession = (): void => {
    // Stopped, reset or unmounted: never open a provider session.
    if (
      phaseRef.current === "closed" ||
      stoppingRef.current ||
      finishedRef.current ||
      !acceptingTranscriptRef.current
    ) {
      dropSocket();
      return;
    }
    clearReconnect();
    socket.emit(TranscriberClientEvents.AUDIO_START, {
      inputLanguage: inputLanguage,
      noiseReduction: noiseReduction,
      userEmail: userData?.email,
      transcriptionProvider: transcriptionProvider,
      noiseSuppression: noiseSuppression,
      diarize: diarize,
    });
  };

  const providerReady = (): void => {
    const phase = phaseRef.current;
    if (phase === "closed" || stoppingRef.current) return;
    let backfill = false;
    if (phase === "connecting") {
      // What was said while connecting goes first, from the recording's start.
      sessionOffsetRef.current = 0;
      sentSampleRef.current = 0;
      ackSampleRef.current = 0;
      aliveSampleRef.current = 0;
      flushHeldAudio();
    } else {
      // A new session after a lost connection: what the last one missed is
      // sent first, so its segment times start where the gap starts.
      accumulatorRef.current.reset();
      backfill = startGap();
    }
    phaseRef.current = "live";
    reconnectAttemptRef.current = 0;
    setLiveTranscriptPaused(false);
    setIsProviderReady(true);
    if (backfill) pumpBackfill();
    else if (isAudioTranscriptionPaused.current && audioStreamRef.current)
      startSilenceKeepAlive();
  };

  /**
   * Decides how the new session starts after a lost connection: with the
   * missed audio (true: an "Adding …" line until its lines are in), or, for
   * a gap too long or not captured, with new audio after a "Paused …" marker.
   */
  const backfillLimits = () =>
    BACKFILL_LIMITS[transcriptionProvider] ??
    BACKFILL_LIMITS[TranscriptionProviderName.DEEPGRAM];

  const startGap = (): boolean => {
    const rate = sampleRateRef.current;
    const history = historyRef.current;
    const now = history.end;
    const from = Math.min(rewindSample(), now);
    // Recorded time the capture never delivered (a frozen page).
    const missing = Math.max(0, clockLag() - clockLagRef.current);
    const { addGap } = useTranscriptionStreamingStore.getState();
    const startAt = (position: number): void => {
      sessionOffsetRef.current = position / rate;
      sentSampleRef.current = position;
      ackSampleRef.current = position;
      aliveSampleRef.current = position;
      clockLagRef.current = clockLag();
    };
    const captured = missing <= BACKFILL_MAX_MISSING_SECONDS;
    if (captured && now - from < rate / 2) {
      // Nothing worth sending again.
      startAt(now);
      return false;
    }
    if (
      !captured ||
      from < history.start ||
      now - from > GAP_CLIP_MAX_SECONDS * rate
    ) {
      // Lines after the marker follow the recording's clock.
      const resumeAt = now + Math.round(missing * rate);
      addGap({ from: from / rate, to: resumeAt / rate, status: "paused" });
      history.jumpTo(resumeAt);
      capturedSecondsRef.current = resumeAt / rate;
      startAt(resumeAt);
      return false;
    }
    const gapId = addGap({
      from: from / rate,
      to: now / rate,
      status: "adding",
    });
    if (
      backfillFailuresRef.current >= BACKFILL_MAX_FAILURES ||
      now - from > backfillLimits().maxSeconds * rate
    ) {
      // Too long for the live provider to catch up on: the API transcribes
      // the gap on its own while the new session hears new audio only.
      const audio = history.slice(from, now);
      startAt(now);
      if (audio) requestGapClip(gapId, audio, from / rate);
      else useTranscriptionStreamingStore.getState().markGapPaused(gapId);
      return false;
    }
    backfillRef.current = { gapId, to: now / rate };
    startAt(from);
    return true;
  };

  /**
   * The gap's lines from the API go in where the gap is; if that fails, the
   * gap stays as a "Paused …" marker (the note still has it).
   */
  const requestGapClip = (
    gapId: number,
    audio: Int16Array,
    fromSeconds: number,
  ): void => {
    const controller = new AbortController();
    gapClipsRef.current.set(gapId, controller);
    const store = useTranscriptionStreamingStore.getState;
    transcribeGapClip({
      audio,
      sampleRate: sampleRateRef.current,
      inputLanguage,
      diarize,
      signal: controller.signal,
    }).then(
      (segments) => {
        if (controller.signal.aborted) return;
        gapClipsRef.current.delete(gapId);
        const lines = segments
          .filter((segment) => segment.text.trim())
          .map((segment) => ({
            // The live transcript's diarized format ("Speaker 0: …").
            text:
              segment.speaker === null
                ? segment.text
                : `Speaker ${segment.speaker}: ${segment.text}`,
            startTime: fromSeconds + segment.start,
          }));
        if (lines.length) store().fillGap(gapId, lines);
        else store().removeGap(gapId);
      },
      () => {
        if (controller.signal.aborted) return;
        gapClipsRef.current.delete(gapId);
        store().markGapPaused(gapId);
      },
    );
  };

  /** Sends the gap a chunk at a time, faster than real time, then hands over to live audio. */
  const pumpBackfill = (): void => {
    backfillTimerRef.current = null;
    const gap = backfillRef.current;
    if (!gap || phaseRef.current !== "live" || stoppingRef.current) return;
    const history = historyRef.current;
    const from = sentSampleRef.current;
    if (history.end - from >= CHUNK_SAMPLES) {
      const body = history.slice(from, from + CHUNK_SAMPLES);
      if (!body) {
        // Cannot happen while the history holds the gap; fail like a loss.
        connectionLost();
        return;
      }
      socket.emit(TranscriberClientEvents.AUDIO_CHUNK, { body, isLast: false });
      sentSampleRef.current += body.length;
      const { speed } = backfillLimits();
      backfillTimerRef.current = window.setTimeout(
        pumpBackfill,
        (CHUNK_SAMPLES / sampleRateRef.current / speed) * 1000,
      );
      return;
    }
    // Caught up: the rest goes out with the next live audio.
    const rest = history.slice(from, history.end);
    accumulatorRef.current.reset();
    if (rest?.length) accumulatorRef.current.addPcm(rest);
    backfillRef.current = null;
    backfillFailuresRef.current = 0;
    settlingGapRef.current = gap;
    settleTimerRef.current = window.setTimeout(settleGap, BACKFILL_SETTLE_MS);
    if (isAudioTranscriptionPaused.current && audioStreamRef.current)
      startSilenceKeepAlive();
  };

  /** The gap's lines are in (or had their time): "Adding …" goes. */
  const settleGap = (): void => {
    if (settleTimerRef.current !== null) {
      window.clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
    const gap = settlingGapRef.current;
    settlingGapRef.current = null;
    if (gap) useTranscriptionStreamingStore.getState().removeGap(gap.gapId);
  };
  const settleGapRef = useRef(settleGap);
  settleGapRef.current = settleGap;

  const providerReadyRef = useRef(providerReady);
  providerReadyRef.current = providerReady;
  const markAliveRef = useRef(markAlive);
  markAliveRef.current = markAlive;
  const markAcknowledgedRef = useRef(markAcknowledged);
  markAcknowledgedRef.current = markAcknowledged;

  const pauseStream = (): void => {
    isAudioTranscriptionPaused.current = true;
    lastPcmAtRef.current = null;
    coverageRef.current.pause();
    // Words still being recognised belong to the moment before the pause;
    // their finished segment (if any) still arrives as transcript:completed.
    clearPartialText();
    startSilenceKeepAlive();
  };

  const setStream = (stream: MediaStream): void => {
    const wasPaused = isAudioTranscriptionPaused.current;
    stopSilenceKeepAlive();
    isAudioTranscriptionPaused.current = false;
    if (stoppingRef.current) return;
    coverageRef.current.start();
    if (audioStreamRef.current) {
      // Resume the capture without replacing the session or its earlier text.
      if (wasPaused) captureRef.current?.resume();
      return;
    }
    acceptingTranscriptRef.current = true;
    resetStreamText();
    audioStreamRef.current = stream;
    const accumulator = accumulatorRef.current;
    const capture = startPcmCapture(stream, (pcm): void => {
      if (isAudioTranscriptionPaused.current) return;
      const at = performance.now();
      if (lastPcmAtRef.current !== null && at - lastPcmAtRef.current > STALL_MS)
        stallGraceUntilRef.current = at + STALL_GRACE_MS;
      lastPcmAtRef.current = at;
      capturedSecondsRef.current += pcm.length / capture.sampleRate;
      const phase = phaseRef.current;
      if (phase === "closed") return;
      // Kept a while, to send again what a lost connection missed.
      historyRef.current.append(pcm);
      if (phase === "connecting") {
        coverageRef.current.addSamples(pcm.length, capture.sampleRate);
        accumulator.addPcm(pcm);
        heldSamplesRef.current += pcm.length;
        if (heldSamplesRef.current > capture.sampleRate * PRE_READY_MAX_SECONDS)
          latchRecovery("unavailable");
        return;
      }
      if (phase !== "live") return;
      coverageRef.current.addSamples(pcm.length, capture.sampleRate);
      // A gap is being sent: this audio follows it from the history.
      if (backfillRef.current) return;
      accumulator.addPcm(pcm);
      if (accumulator.isReadyToSend) {
        // Stop feeding the provider gappy audio as soon as it is detected.
        if (recoveryRef.current || checkCaptureCoverage()) submitChunk();
      }
    });
    sampleRateRef.current = capture.sampleRate;
    captureRef.current = capture;
  };

  useEffect(() => {
    // One audio:start per connect. Without the off(), a re-run of this effect
    // (React StrictMode in dev, or a quick remount) leaves a second listener,
    // the server opens two provider sessions, and the unused one later sends
    // an empty transcript:end that the recorder takes as the final transcript.
    // Every (re)connect opens one provider session.
    acceptingTranscriptRef.current = true;
    socket.on("connect", startSession);
    return (): void => {
      socket.off("connect", startSession);
      stopSilenceKeepAlive();
      clearReconnect();
      clearBackfill();
      abortGapClips();
      teardownProcessor();
      acceptingTranscriptRef.current = false;
    };
  }, [
    stopSilenceKeepAlive,
    teardownProcessor,
    clearReconnect,
    clearBackfill,
    abortGapClips,
  ]);

  const handlers = useMemo(() => {
    return {
      [TranscriberServerEvents.CONNECT_ERROR]: (): void => {
        recoverAudio();
      },
      disconnect: (): void => {
        // Our own close (latchRecovery, Stop) needs nothing more.
        if (phaseRef.current === "closed") return;
        recoverAudio();
      },
      [TranscriberServerEvents.PROVIDER_UNAVAILABLE]: recoverAudio,
      // Any transcriber error (ROUTE_UNAVAILABLE, DRAINING, SESSION_ERROR, ...)
      // means live text can no longer be trusted to be complete. Without this
      // a mid-visit failure kept the recorder "live" and Stop saved only the
      // text received before it.
      [TranscriberServerEvents.ERROR]: recoverAudio,
      [TranscriberServerEvents.PROVIDER_READY]: (): void => {
        providerReadyRef.current();
      },
      [TranscriberServerEvents.PROVIDER_WAIT]: (): void => {
        if (phaseRef.current !== "live") setIsProviderReady(false);
      },
      // In-progress words, shown after the finished text and never saved.
      // Ignored once stopping, paused or finished so stale words cannot
      // reappear after the live text was settled.
      [TranscriberServerEvents.TRANSCRIPT_PARTIAL]: (
        data: TranscriptPartialMessage,
      ): void => {
        if (
          !acceptingTranscriptRef.current ||
          phaseRef.current !== "live" ||
          finishedRef.current ||
          stoppingRef.current ||
          isAudioTranscriptionPaused.current
        ) {
          return;
        }
        markAliveRef.current();
        setPartialText(data);
      },
      [TranscriberServerEvents.TRANSCRIPT_COMPLETED]: (
        data: TranscriptCompletedMessage,
      ): void => {
        if (!acceptingTranscriptRef.current || phaseRef.current !== "live")
          return;
        markAcknowledgedRef.current();
        settlePartialText(data.itemId);
        const startTime = data.startTime + sessionOffsetRef.current;
        const settling = settlingGapRef.current;
        if (settling && startTime >= settling.to - 0.5) settleGapRef.current();
        if (!data.transcription?.trim()) return;
        setStreamText({ text: data.transcription, startTime });
      },
      [TranscriberServerEvents.TRANSCRIPT_END]: (
        data: TranscriptEndMessage,
      ): void => {
        if (
          !acceptingTranscriptRef.current ||
          recoveryRef.current ||
          finishedRef.current
        )
          return;
        if (!stoppingRef.current || !data.transcription?.trim()) {
          recoverAudio();
          return;
        }
        // The transcriber lost or never finished part of the audio it
        // received (for example a finalization timeout): transcribe the full
        // recording instead. Older transcribers send no coverage.
        if (data.coverage?.complete === false) {
          latchRecovery("incomplete");
          return;
        }
        finishSteamText();
        setTranscription(data.transcription);
        finishedRef.current = true;
        setFinished(true);
      },
    };
  }, [
    finishSteamText,
    setStreamText,
    setPartialText,
    settlePartialText,
    recoverAudio,
    latchRecovery,
  ]);

  useEffect(() => {
    Object.entries(handlers).forEach(([event, handler]) => {
      socket.on(event, handler);
    });
    return (): void => {
      Object.entries(handlers).forEach(([event, handler]) => {
        socket.off(event, handler);
      });
    };
  }, [socket, handlers]);

  return {
    audioStream: audioStreamRef.current,
    setStream,
    transcription: transcription,
    finishSession,
    finished,
    reset,
    pauseStream,
    isProviderReady,
    requiresAudioRecovery,
    audioRecoveryReason,
    recoverAudio,
    liveTranscriptPaused,
  };
};
